import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import { Search as SearchIcon, InfoOutlined } from '@mui/icons-material';
import axios from 'axios';
import OptionStrategyCard from '../components/OptionStrategyCard';

const OUTLOOKS = [
  { value: 'auto', label: 'Auto (from indicators)' },
  { value: 'bullish', label: 'Bullish' },
  { value: 'bearish', label: 'Bearish' },
  { value: 'neutral', label: 'Neutral' },
];

const OptionsRecommendations = () => {
  const { symbol: routeSymbol } = useParams();
  const navigate = useNavigate();
  const [symbolInput, setSymbolInput] = useState(routeSymbol || '');
  const [symbol, setSymbol] = useState(routeSymbol || '');
  const [outlook, setOutlook] = useState('auto');
  const [recommendations, setRecommendations] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState(null);

  const fetchRecommendations = useCallback(async (sym, selectedOutlook) => {
    if (!sym) return;
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (selectedOutlook && selectedOutlook !== 'auto') params.outlook = selectedOutlook;
      const res = await axios.get(`/options/recommendations/${sym.toUpperCase()}`, { params });
      setRecommendations(res.data.recommendations || []);
      setStatus(res.data.status);
      setMeta({
        outlook: res.data.outlook,
        outlook_source: res.data.outlook_source,
        volatility_profile: res.data.volatility_profile,
        stale: res.data.stale,
      });
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to fetch options recommendations');
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (symbol) fetchRecommendations(symbol, outlook);
  }, [symbol, outlook, fetchRecommendations]);

  const handleSearch = () => {
    const sym = symbolInput.trim().toUpperCase();
    if (sym) {
      setSymbol(sym);
      navigate(`/options/${sym}`);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>Options Strategy Recommendations</Typography>

      {/* Persistent informational-only notice */}
      <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 2 }}>
        Informational only — this tool does not place options orders. Recommendations are
        limited to Alpaca Option Level 2 strategies.
      </Alert>

      {/* Symbol search */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          label="Symbol"
          value={symbolInput}
          onChange={(e) => setSymbolInput(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          size="small"
          fullWidth
        />
        <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>
          Search
        </Button>
      </Box>

      {/* Outlook selector */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Directional outlook</Typography>
        <ToggleButtonGroup
          value={outlook}
          exclusive
          size="small"
          onChange={(e, val) => val && setOutlook(val)}
        >
          {OUTLOOKS.map((o) => (
            <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {meta && (meta.outlook || meta.volatility_profile) && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {meta.outlook && <Chip size="small" label={`Outlook: ${meta.outlook} (${meta.outlook_source})`} />}
          {meta.volatility_profile && <Chip size="small" label={`Volatility: ${meta.volatility_profile}`} />}
          {meta.stale && <Chip size="small" color="warning" label="Showing cached recommendations" />}
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && symbol && status === 'none_available' && (
        <Alert severity="warning">No options recommendations are currently available for {symbol}.</Alert>
      )}

      {!loading && recommendations.map((rec, i) => (
        <OptionStrategyCard key={rec.id || i} rec={rec} />
      ))}

      {!loading && !symbol && (
        <Typography variant="body2" color="text.secondary">
          Enter a stock symbol to see Level 2 options strategy recommendations.
        </Typography>
      )}
    </Container>
  );
};

export default OptionsRecommendations;
